/**
 * Cart Operations Module for Fyncall Widget
 * Handles cart calculations, shipping progress, delivery estimates, and promo codes
 */

import {
    FREE_SHIPPING_THRESHOLD,
    DELIVERY_MIN_BUSINESS_DAYS,
    DELIVERY_MAX_BUSINESS_DAYS,
    SAME_DAY_PROCESSING_CUTOFF,
    PROMO_DEMO_DISCOUNT_RATE
} from '../constants.js';

/**
 * Calculate cart summary totals
 * @param {Object} cartData - Cart data with items and total
 * @param {number} promoDiscount - Applied promo discount amount
 * @returns {Object} Summary with subtotal, discount, total, savings
 */
export function calculateCartSummary(cartData, promoDiscount = 0) {
    const subtotal = cartData.total || 0;
    const discount = promoDiscount || 0;
    const total = subtotal - discount;

    // Calculate savings from compare_at_price
    let totalSavings = 0;
    if (cartData.items) {
        cartData.items.forEach(item => {
            if (item.compare_at_price && item.compare_at_price > item.price) {
                totalSavings += (item.compare_at_price - item.price) * (item.quantity || 1);
            }
        });
    }

    return {
        subtotal,
        discount,
        total,
        savings: totalSavings,
        hasSavings: totalSavings > 0,
        hasDiscount: discount > 0
    };
}

/**
 * Calculate free shipping progress
 * @param {number} cartTotal - Current cart total
 * @param {number} threshold - Free shipping threshold (default from constants)
 * @returns {Object} Progress info
 */
export function calculateFreeShippingProgress(cartTotal, threshold = FREE_SHIPPING_THRESHOLD) {
    const progress = Math.min((cartTotal / threshold) * 100, 100);
    const qualifies = cartTotal >= threshold;
    const remaining = qualifies ? 0 : threshold - cartTotal;

    return {
        progress,
        qualifies,
        remaining,
        message: qualifies
            ? '🎉 You qualify for FREE shipping!'
            : `Add $${remaining.toFixed(2)} more for FREE shipping`,
        shippingText: qualifies ? 'FREE' : 'Calculated at checkout'
    };
}

/**
 * Calculate delivery estimate based on current time
 * @param {Date} now - Current date/time (defaults to now)
 * @returns {Object} Delivery estimate info
 */
export function calculateDeliveryEstimate(now = new Date()) {
    const hour = now.getHours();

    let deliveryStart = new Date(now);
    let deliveryEnd = new Date(now);

    // Add processing time (same day if before cutoff, next day otherwise)
    if (hour >= SAME_DAY_PROCESSING_CUTOFF) {
        deliveryStart.setDate(deliveryStart.getDate() + 1);
        deliveryEnd.setDate(deliveryEnd.getDate() + 1);
    }

    // Add minimum business days
    let businessDaysAdded = 0;
    while (businessDaysAdded < DELIVERY_MIN_BUSINESS_DAYS) {
        deliveryStart.setDate(deliveryStart.getDate() + 1);
        if (deliveryStart.getDay() !== 0 && deliveryStart.getDay() !== 6) {
            businessDaysAdded++;
        }
    }

    // Add maximum business days
    businessDaysAdded = 0;
    while (businessDaysAdded < DELIVERY_MAX_BUSINESS_DAYS) {
        deliveryEnd.setDate(deliveryEnd.getDate() + 1);
        if (deliveryEnd.getDay() !== 0 && deliveryEnd.getDay() !== 6) {
            businessDaysAdded++;
        }
    }

    const options = { month: 'short', day: 'numeric' };
    const startStr = deliveryStart.toLocaleDateString('en-US', options);
    const endStr = deliveryEnd.toLocaleDateString('en-US', options);

    return {
        startDate: deliveryStart,
        endDate: deliveryEnd,
        rangeText: `${startStr} - ${endStr}`,
        minDays: DELIVERY_MIN_BUSINESS_DAYS,
        maxDays: DELIVERY_MAX_BUSINESS_DAYS
    };
}

/**
 * Calculate promo discount amount
 * @param {number} cartTotal - Cart total before discount
 * @param {number} discountRate - Discount rate (default from constants)
 * @returns {number} Discount amount
 */
export function calculatePromoDiscount(cartTotal, discountRate = PROMO_DEMO_DISCOUNT_RATE) {
    return cartTotal * discountRate;
}

/**
 * Validate promo code format
 * @param {string} code - Promo code to validate
 * @returns {Object} Validation result
 */
export function validatePromoCode(code) {
    const trimmedCode = (code || '').trim().toUpperCase();

    if (!trimmedCode) {
        return { valid: false, error: 'Please enter a promo code' };
    }

    if (trimmedCode.length < 3) {
        return { valid: false, error: 'Invalid promo code' };
    }

    return { valid: true, code: trimmedCode };
}

/**
 * Apply promo code via Shopify discount API
 * @param {string} code - Promo code
 * @returns {Promise<Object>} Result with success status
 */
export async function applyPromoCodeToShopify(code) {
    try {
        const response = await fetch('/discount/' + encodeURIComponent(code));
        return { success: response.ok, shopifyApplied: response.ok };
    } catch (error) {
        // Discount endpoint doesn't exist - use demo mode
        return { success: true, shopifyApplied: false, demoMode: true };
    }
}

/**
 * Generate applied promo code HTML
 * @param {string} code - Applied promo code
 * @returns {string} HTML string
 */
export function generateAppliedPromoHTML(code) {
    return `
        <div class="fyncall-cart-promo-applied">
            <span>🏷️ ${code} applied</span>
            <button class="fyncall-cart-promo-remove" data-action="remove-promo">✕</button>
        </div>
    `;
}

/**
 * Calculate item total with quantity
 * @param {Object} item - Cart item
 * @returns {Object} Item totals
 */
export function calculateItemTotal(item) {
    const price = parseFloat(item.price) || 0;
    const quantity = parseInt(item.quantity) || 1;
    const lineTotal = price * quantity;

    let savings = 0;
    if (item.compare_at_price && item.compare_at_price > price) {
        savings = (item.compare_at_price - price) * quantity;
    }

    return {
        price,
        quantity,
        lineTotal,
        savings,
        hasSavings: savings > 0
    };
}

/**
 * Get cart item count
 * @param {Object} cartData - Cart data
 * @returns {number} Total item count
 */
export function getCartItemCount(cartData) {
    if (!cartData || !cartData.items) return 0;
    return cartData.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
}

// Re-export constants for convenience
export {
    FREE_SHIPPING_THRESHOLD,
    DELIVERY_MIN_BUSINESS_DAYS,
    DELIVERY_MAX_BUSINESS_DAYS,
    SAME_DAY_PROCESSING_CUTOFF,
    PROMO_DEMO_DISCOUNT_RATE
};
